import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Modal, Spin, Steps, message } from 'antd';
import {
  useStartPlanMutation,
  useAttachFileMutation,
  useDetachFileMutation,
  useDescribeMutation,
  useSubmitAnswersMutation,
  useSendMessageMutation,
  useRevertPlanMutation,
  useEditDraftMutation,
  useApplyPlanMutation,
  useCancelPlanMutation,
  useGetPlannerTasksQuery,
  useGetPlanQuery,
  planError,
  MODE_LABEL,
  type PlanSession,
  type PlanMode,
  type DraftPlan,
  type ChangeSet,
} from '../features/aiPlanner/plannerApi';
import { useGetCoordinatorsQuery, coordinatorName } from '../features/tasks/tasksApi';
import DescribeStep from './aiPlanner/DescribeStep';
import ProgressPanel from './aiPlanner/ProgressPanel';
import QuestionsStep from './aiPlanner/QuestionsStep';
import PlanStep from './aiPlanner/PlanStep';
import ChangeStep from './aiPlanner/ChangeStep';
import { PlusCircleIcon, TrashIcon, CheckCircleIcon } from '../assets/icons';

interface Props {
  engagementId: number | string;
  engagementName: string;
  engagementDate?: string | null;
  /** the description saved on the engagement — pre-fills the Describe box in plan mode */
  engagementDescription?: string | null;
  /** the engagement already has tasks -> offer Add / Remove a program instead of a fresh plan */
  hasTasks: boolean;
  open: boolean;
  onClose: () => void;
  onApplied?: () => void;
}

const STEPS: Record<PlanMode, string[]> = {
  plan: ['Describe', 'Plan & refine', 'Done'],
  add_program: ['Describe', 'Questions', 'Changes', 'Done'],
  remove_program: ['Describe', 'Changes', 'Done'],
};

function stepFor(mode: PlanMode, s: PlanSession | null): number {
  if (!s) return 0;
  const names = STEPS[mode];
  switch (s.status) {
    case 'describing': return 0;
    case 'questioning': return names.indexOf('Questions');
    case 'drafting':
    case 'review': return names.indexOf(mode === 'plan' ? 'Plan & refine' : 'Changes');
    case 'applied': return names.length - 1;
    default: return 0;
  }
}

/** Session errors come back with the session in the body (502) — keep the transcript. */
function sessionFromError(e: unknown): PlanSession | null {
  const d = (e as { data?: PlanSession })?.data;
  return d && typeof d === 'object' && 'id' in d ? d : null;
}

export default function AiPlannerModal({
  engagementId, engagementName, engagementDate, engagementDescription, hasTasks, open, onClose, onApplied,
}: Props) {
  const [mode, setMode] = useState<PlanMode | null>(null);
  const [session, setSession] = useState<PlanSession | null>(null);

  const [startPlan, startState] = useStartPlanMutation();
  const [attachFile] = useAttachFileMutation();
  const [detachFile] = useDetachFileMutation();
  const [describe, describeState] = useDescribeMutation();
  const [submitAnswers, answersState] = useSubmitAnswersMutation();
  const [sendMessage, sendState] = useSendMessageMutation();
  const [revertPlan, revertState] = useRevertPlanMutation();
  const [editDraft] = useEditDraftMutation();
  const [applyPlan, applyState] = useApplyPlanMutation();
  const [cancelPlan] = useCancelPlanMutation();

  const { data: coordinators = [] } = useGetCoordinatorsQuery(engagementId, { skip: !open || !engagementId });
  const coordOptions = useMemo(() => coordinators.map((c) => ({ value: c.id, label: coordinatorName(c) })), [coordinators]);
  const { data: existingData } = useGetPlannerTasksQuery(engagementId, { skip: !open || !hasTasks || mode === 'plan' });
  const existing = existingData?.tasks ?? [];

  // reset ONLY when the modal opens; a fresh engagement goes straight to the description.
  // (hasTasks flips to true right after "Create tasks" — that must not reset the modal.)
  useEffect(() => {
    if (!open) return;
    setSession(null);
    setMode(hasTasks ? null : 'plan');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // create the session as soon as the mode is known (files attach to it)
  const starting = useRef(false);
  useEffect(() => {
    if (!open || !mode || session || starting.current) return;
    starting.current = true;
    startPlan({ engagement: engagementId, mode }).unwrap()
      .then(setSession)
      .catch((e) => message.error(planError(e)))
      .finally(() => { starting.current = false; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, session]);

  // while the backend works in the background, poll the session for progress events
  const drafting = session?.status === 'drafting';
  const { data: polled } = useGetPlanQuery(session?.id ?? 0, { skip: !drafting, pollingInterval: 1500 });
  useEffect(() => {
    if (!polled || !drafting || polled.id !== session?.id) return;
    const changed = polled.status !== session.status
      || (polled.progress?.length ?? 0) !== (session.progress?.length ?? 0);
    if (changed) setSession(polled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polled]);
  const [showDone, setShowDone] = useState(false);
  useEffect(() => {
    // leave the finished timeline on screen for a moment before the plan opens
    if (session?.status === 'review' && (session.progress?.length ?? 0) > 0 && !showDone) {
      setShowDone(true);
      const id = window.setTimeout(() => setShowDone(false), 900);
      return () => window.clearTimeout(id);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status]);

  const step = mode ? stepFor(mode, session) : 0;
  const thinking = describeState.isLoading || answersState.isLoading;

  /* ------------------------------------------------------------- actions */
  const run = async (p: Promise<PlanSession>) => {
    try { setSession(await p); }
    catch (e) {
      const s = sessionFromError(e);
      if (s) setSession(s);
      message.error(planError(e));
    }
  };

  const doDescribe = (description: string) => run(describe({ id: session!.id, description }).unwrap());
  const doAnswers = (answers: Record<string, string>) => run(submitAnswers({ id: session!.id, answers }).unwrap());
  const doSend = (text: string, draft: DraftPlan | ChangeSet) => run(sendMessage({ id: session!.id, text, draft }).unwrap());
  const doRevert = (version: number) => run(revertPlan({ id: session!.id, version }).unwrap());

  const doApplyPlan = async (finalDraft: DraftPlan, updateEngagementDate: boolean) => {
    if (!session) return;
    try {
      await editDraft({ id: session.id, draft_plan: finalDraft }).unwrap();
      const res = await applyPlan({ id: session.id, update_engagement_date: updateEngagementDate }).unwrap();
      setSession(res);
      onApplied?.();
    } catch (e) { message.error(planError(e)); }
  };
  const doApplyChange = async (final: ChangeSet) => {
    if (!session) return;
    try {
      await editDraft({ id: session.id, change_set: final }).unwrap();
      const res = await applyPlan({ id: session.id }).unwrap();
      setSession(res);
      onApplied?.();
    } catch (e) { message.error(planError(e)); }
  };

  const close = () => {
    if (session && session.status !== 'applied' && session.status !== 'cancelled') {
      cancelPlan(session.id).catch(() => undefined);
    }
    onClose();
  };

  /* ------------------------------------------------------------- render */
  const title = mode ? MODE_LABEL[mode] : 'Change plan';
  const wide = step > 0 && session?.status !== 'applied' && (mode === 'plan' || session?.status === 'review');

  return (
    <Modal
      open={open} onCancel={close} width={wide ? 'min(1480px, 96vw)' : 760} maskClosable={false} footer={null} destroyOnClose
      style={wide ? { top: 20, paddingBottom: 0 } : undefined}
      title={<span>{title} — <span className="text-brand">{engagementName}</span></span>}
    >
      {/* mode chooser (only when the engagement already has tasks) */}
      {!mode && (
        <div className="flex flex-col gap-4 py-2">
          <p className="text-sm text-ink-secondary">
            This engagement already has a plan. Tell the AI what changed and it will propose the exact tasks to add,
            change or remove — you review everything before anything is applied.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <button type="button" onClick={() => setMode('add_program')}
              className="flex flex-col gap-2 rounded-lg border border-line-soft bg-white p-4 text-left transition-colors hover:border-brand hover:bg-brand-shade/40">
              <PlusCircleIcon className="size-6 text-brand" />
              <span className="text-sm font-semibold text-ink">Add a program</span>
              <span className="text-xs text-ink-secondary">A new item joins the engagement. The AI asks 2–3 questions, then plans the new tasks and adjusts existing ones.</span>
            </button>
            <button type="button" onClick={() => setMode('remove_program')}
              className="flex flex-col gap-2 rounded-lg border border-line-soft bg-white p-4 text-left transition-colors hover:border-brand hover:bg-brand-shade/40">
              <TrashIcon className="size-6 text-[#DC2626]" />
              <span className="text-sm font-semibold text-ink">Remove a program</span>
              <span className="text-xs text-ink-secondary">Part of the engagement is dropped. The AI proposes which tasks to remove or shrink. Finished work is never deleted.</span>
            </button>
          </div>
        </div>
      )}

      {mode && (
        <>
          <Steps size="small" current={step} className="my-4" items={STEPS[mode].map((t) => ({ title: t }))} />

          {startState.isLoading && !session && (
            <div className="py-10 text-center text-sm text-ink-secondary"><Spin /> <span className="ml-2">Preparing…</span></div>
          )}

          {session && (session.status === 'describing' || (session.status === 'failed' && !session.progress?.length)) && (
            <Spin spinning={thinking} tip="Reading the existing plan and preparing a few questions…">
              <DescribeStep
                key={session.id} mode={mode} engagementName={engagementName}
                initialText={mode === 'plan' ? (engagementDescription ?? '') : ''}
                attachments={session.attachments ?? []}
                onAttach={(file) => attachFile({ id: session.id, file }).unwrap().then(setSession)}
                onDetach={(name) => detachFile({ id: session.id, name }).unwrap().then(setSession)}
                onContinue={doDescribe} loading={thinking}
              />
            </Spin>
          )}

          {session && session.status === 'questioning' && (
            <QuestionsStep key={session.id} questions={session.questions ?? []} onContinue={doAnswers} loading={thinking} />
          )}

          {/* background run: live stage timeline (stays a moment after it finishes) */}
          {session && (session.status === 'drafting' || showDone
            || (session.status === 'failed' && (session.progress?.length ?? 0) > 0)) && (
            <ProgressPanel
              mode={mode} events={session.progress ?? []} startedAt={session.progress?.[0]?.at}
              failed={session.status === 'failed' ? (session.error || 'Unknown error') : null}
              onRetry={() => setSession(null)}
            />
          )}

          {session && session.status === 'review' && !showDone && mode === 'plan' && (
            <PlanStep
              key={session.id} session={session} coordinators={coordOptions} engagementDate={engagementDate}
              onSend={doSend} onRevert={doRevert} onApply={doApplyPlan} onCancel={close}
              sending={sendState.isLoading || revertState.isLoading} applying={applyState.isLoading}
            />
          )}

          {session && session.status === 'review' && !showDone && mode !== 'plan' && (
            <ChangeStep
              key={session.id} session={session} existing={existing} coordinators={coordOptions} engagementDate={engagementDate}
              onSend={doSend} onRevert={doRevert} onApply={doApplyChange} onCancel={close}
              sending={sendState.isLoading || revertState.isLoading} applying={applyState.isLoading}
            />
          )}

          {session?.status === 'applied' && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <CheckCircleIcon className="size-10 text-[#16a34a]" />
              <div className="text-lg font-semibold text-ink">{mode === 'plan' ? 'Tasks created' : 'Changes applied'}</div>
              <p className="max-w-md text-sm text-ink-secondary">
                {mode === 'plan'
                  ? `${session.count ?? 0} task(s) and sub-task(s) were added to this engagement.`
                  : `${session.count ?? 0} added · ${session.modified_task_ids?.length ?? 0} changed · ${session.removed_task_ids?.length ?? 0} removed`
                    + ((session.flagged_task_ids?.length ?? 0) > 0 ? ` · ${session.flagged_task_ids!.length} marked as no longer needed` : '')}
              </p>
              {(session.notes?.length ?? 0) > 0 && (
                <Alert type="warning" showIcon className="w-full text-left" message="Please note"
                  description={<ul className="ml-4 list-disc">{session.notes!.map((n, i) => <li key={i}>{n}</li>)}</ul>} />
              )}
              <Button type="primary" onClick={() => { onApplied?.(); onClose(); }}>Done</Button>
            </div>
          )}

          {session?.status === 'failed' && !(session.progress?.length) && (
            <Alert className="mt-3" type="error" showIcon message="The AI could not finish"
              description={session.error || 'Unknown error'}
              action={<Button size="small" onClick={() => { setSession(null); }}>Start again</Button>} />
          )}
        </>
      )}
    </Modal>
  );
}
